import httpService from "../../../../services/httpService";

export interface JobOpportunity {
  opportunity_id?: string;
  company_id?: string | null;
  company_name?: string | null;
  title: string;
  department?: string | null;
  for_organization_id?: string | null;
  for_organization_name?: string | null;
  application_deadline: string;
  vacancies?: number | null;
  work_location: string;
  hiring_type: string;
  job_type: string;
  min_years_experience: number;
  max_years_experience?: number | null;
  education_level?: string | null;
  payment_type: string;
  min_ctc?: number | null;
  max_ctc?: number | null;
  min_hourly_rate?: number | null;
  max_hourly_rate?: number | null;
  description?: string | null;
  skills_required: string[];
  skills_preferred: string[];
  work_type?: string | null;
  status?: string;
  creation_method?: string;
  source_document_url?: string;
  source_document_name?: string;
  stages?: string[];
  has_prospective_candidates?: boolean;
  prospective_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface JobOpportunityListView {
  total_no_of_records: number;
  no_of_records: number;
  records: JobOpportunity[];
}

export type HiringType = "Internal" | "Sub contractor";
export type JobType = "Full time" | "Part time" | "Contractor";
export type PaymentType = "Annual" | "Hourly";
export type WorkType = "Remote" | "Hybrid" | "On-site";
export type OpportunityStatus = "Draft" | "Open" | "Closed";
export type JobStage = "Applied" | "Screening" | "Interview" | "Offer" | "Hired" | "Rejected";

class JobOpportunityClient {
  private basePath = "/job_opportunities";

  async createOpportunity(data: JobOpportunity): Promise<JobOpportunity> {
    const response = await httpService.post<JobOpportunity>(this.basePath, data);
    return response.data;
  }

  async extractFromDocument(file: File): Promise<{ file_id: string; message: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await httpService.post<{ file_id: string; message: string }>(`${this.basePath}/extract`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async trackUploads(params?: { limit?: number; start_record?: number }): Promise<any> {
    const response = await httpService.get<any>(`${this.basePath}/upload/track`, { params });
    return response.data;
  }

  async getUploadStatus(fileId: string): Promise<any> {
    const response = await httpService.get<any>(`${this.basePath}/upload/${fileId}/status`);
    return response.data;
  }

  async listOpportunities(params?: {
    page_number?: number;
    limit?: number;
    status?: string;
    organization_id?: string;
    title?: string;
  }): Promise<JobOpportunityListView> {
    const response = await httpService.get<JobOpportunityListView>(this.basePath, { params });
    return response.data;
  }

  async getOpportunity(opportunityId: string): Promise<JobOpportunity> {
    const response = await httpService.get<JobOpportunity>(`${this.basePath}/${opportunityId}`);
    return response.data;
  }

  async updateOpportunity(opportunityId: string, data: Partial<JobOpportunity>): Promise<JobOpportunity> {
    const response = await httpService.post<JobOpportunity>(`${this.basePath}/${opportunityId}`, data);
    return response.data;
  }

  async deleteOpportunity(opportunityId: string): Promise<void> {
    await httpService.delete(`${this.basePath}/${opportunityId}`);
  }

  async getStages(): Promise<string[]> {
    const response = await httpService.get<string[]>(`${this.basePath}/stages`);
    return response.data;
  }

  async getRejectionReasons(): Promise<string[]> {
    const response = await httpService.get<string[]>(`${this.basePath}/rejection_reasons`);
    return response.data;
  }
}

const client = new JobOpportunityClient();
export default client;
